import React, { useCallback, useEffect, useMemo } from 'react'

import { Controller, useForm, useWatch } from 'react-hook-form'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { Input } from '@/components/common/TextInputV2'
import ErrorMessage from '@/components/form/ErrorMessage'

import type { OrganizationMCPServer } from '@/types/MCPServer'

export const SALESFORCE_COMMERCIAL_LOGIN_HOST = 'login.salesforce.com'
export const SALESFORCE_SANDBOX_LOGIN_HOST = 'test.salesforce.com'

export type SalesforceEnvironment = 'commercial' | 'sandbox' | 'custom'

export interface SalesforceConnectionFormValues {
  customLoginHost: string
  environment: SalesforceEnvironment
}

export const SALESFORCE_ENVIRONMENT_LABELS: Record<
  SalesforceEnvironment,
  string
> = {
  commercial: 'Commercial Production',
  custom: 'Government Cloud / Custom',
  sandbox: 'Sandbox',
}

export const isSalesforceEnvironment = (
  value: unknown,
): value is SalesforceEnvironment =>
  value === 'commercial' || value === 'sandbox' || value === 'custom'

export const getSalesforceLoginHost = (
  providerConfig: OrganizationMCPServer['oauth_provider_config'],
): string => {
  const loginHost = providerConfig?.login_host
  return typeof loginHost === 'string' && loginHost.trim()
    ? loginHost.trim()
    : SALESFORCE_COMMERCIAL_LOGIN_HOST
}

export const getSalesforceInstanceUrl = (
  providerConfig: OrganizationMCPServer['oauth_provider_config'],
): string | null => {
  const instanceUrl = providerConfig?.instance_url
  return typeof instanceUrl === 'string' && instanceUrl.trim()
    ? instanceUrl.trim()
    : null
}

export const getSalesforceEnvironment = (
  providerConfig: OrganizationMCPServer['oauth_provider_config'],
): SalesforceEnvironment => {
  const configuredEnvironment = providerConfig?.salesforce_environment
  if (isSalesforceEnvironment(configuredEnvironment)) {
    return configuredEnvironment
  }

  const loginHost = getSalesforceLoginHost(providerConfig)
  if (loginHost === SALESFORCE_SANDBOX_LOGIN_HOST) {
    return 'sandbox'
  }
  if (loginHost === SALESFORCE_COMMERCIAL_LOGIN_HOST) {
    return 'commercial'
  }
  return 'custom'
}

export const normalizeSalesforceLoginHost = (value: string): string | null => {
  const raw = value.trim()
  if (!raw) {
    return null
  }

  try {
    const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`)
    if (
      !['http:', 'https:'].includes(parsed.protocol) ||
      !parsed.hostname ||
      parsed.pathname !== '/' ||
      parsed.search ||
      parsed.hash
    ) {
      return null
    }
    return parsed.hostname
  } catch {
    return null
  }
}

export const buildSalesforceProviderConfig = ({
  baseProviderConfig,
  values,
}: {
  baseProviderConfig: OrganizationMCPServer['oauth_provider_config']
  values: SalesforceConnectionFormValues
}): Record<string, any> | null => {
  const configuredLoginHost =
    values.environment === 'commercial'
      ? SALESFORCE_COMMERCIAL_LOGIN_HOST
      : values.environment === 'sandbox'
        ? SALESFORCE_SANDBOX_LOGIN_HOST
        : normalizeSalesforceLoginHost(values.customLoginHost)

  if (!configuredLoginHost) {
    return null
  }

  return {
    ...(baseProviderConfig || {}),
    login_host: configuredLoginHost,
    salesforce_environment: values.environment,
  }
}

export const getSalesforceConnectionDefaultValues = (
  providerConfig: OrganizationMCPServer['oauth_provider_config'],
): SalesforceConnectionFormValues => {
  const environment = getSalesforceEnvironment(providerConfig)
  return {
    customLoginHost:
      environment === 'custom' ? getSalesforceLoginHost(providerConfig) : '',
    environment,
  }
}

const DefaultDescription: React.FC = () => (
  <>
    Choose the Salesforce environment for this integration. This connection will
    be used for background syncs.
  </>
)

interface SalesforceConnectionDialogProps {
  defaultValues: SalesforceConnectionFormValues
  description?: React.ReactNode
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (values: SalesforceConnectionFormValues) => void | Promise<void>
  open: boolean
  submitLabel?: string
  title?: string
}

export const SalesforceConnectionDialog: React.FC<
  SalesforceConnectionDialogProps
> = ({
  defaultValues,
  description = <DefaultDescription />,
  isSubmitting,
  onOpenChange,
  onSubmit,
  open,
  submitLabel = 'Continue',
  title = 'Connect Salesforce',
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<SalesforceConnectionFormValues>({ defaultValues })

  const environment = useWatch({
    control,
    name: 'environment',
  })

  useEffect(() => {
    if (open) {
      reset(defaultValues)
    }
  }, [defaultValues, open, reset])

  const validateCustomLoginHost = useCallback(
    (value: string) => {
      if (environment !== 'custom') {
        return true
      }
      return (
        !!normalizeSalesforceLoginHost(value) ||
        'Enter a valid Salesforce login domain.'
      )
    },
    [environment],
  )

  const handleFormSubmit = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[440px] p-0">
        <form onSubmit={handleFormSubmit}>
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-5 space-y-4">
            <div className="space-y-1.5">
              <label
                className="text-sm font-medium text-primary-900 dark:text-primary-100"
                htmlFor="salesforce-environment"
              >
                Environment
              </label>
              <Controller
                control={control}
                name="environment"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    disabled={isSubmitting}
                    onValueChange={(value) => {
                      if (isSalesforceEnvironment(value)) {
                        field.onChange(value)
                      }
                    }}
                  >
                    <SelectTrigger id="salesforce-environment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial">
                        {SALESFORCE_ENVIRONMENT_LABELS.commercial}
                      </SelectItem>
                      <SelectItem value="sandbox">
                        {SALESFORCE_ENVIRONMENT_LABELS.sandbox}
                      </SelectItem>
                      <SelectItem value="custom">
                        {SALESFORCE_ENVIRONMENT_LABELS.custom}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {environment === 'custom' ? (
              <div className="space-y-1.5">
                <label
                  className="text-sm font-medium text-primary-900 dark:text-primary-100"
                  htmlFor="salesforce-login-host"
                >
                  Login Domain
                </label>
                <Input
                  id="salesforce-login-host"
                  placeholder="customer.my.salesforce.com"
                  disabled={isSubmitting}
                  aria-invalid={!!errors.customLoginHost}
                  {...register('customLoginHost', {
                    validate: validateCustomLoginHost,
                  })}
                />
                {errors.customLoginHost?.message ? (
                  <ErrorMessage>{errors.customLoginHost.message}</ErrorMessage>
                ) : null}
              </div>
            ) : null}
          </div>
          <DialogFooter className="px-5 py-4 border-t border-primary-200 dark:border-primary-800">
            <DialogClose asChild={true}>
              <Button
                type="button"
                variant="outline-compact"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary-compact"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
